import { WizardLoadingScreen } from './WizardLoadingScreen';
import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  reload
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from '../services/firebase';
import { Mail, Lock, User as UserIcon, Camera, ChevronRight, ArrowLeft, Check, RefreshCw, ArrowRight, Edit2, Copy } from 'lucide-react';
import { LazyCropper } from './LazyCropper';
import { getCroppedImg } from '../utils/imageUtils';
import { User } from '../types';
import { createTripNotifications } from '../services/notificationService';
import { getPendingInvitations } from '../services/userLookup';
import { useAuth } from '../utils/AuthContext';
import { LocationSearch } from './LocationSearch';
import { TalismanMark, TalismanLogo } from './Icons';
import iosInstallHtml from '../assets/ios-install-guide.html?raw';
import { CardTintStreaks, FIELD_AUTH, AUTH_ERROR, fieldBorder } from './UI';
import { userMessage } from '../utils/userMessage';

interface AuthScreenProps {
  onSuccess: (user: User) => void;
}

type Step = 'auth' | 'email-verification' | 'onboarding-info' | 'onboarding-photo' | 'onboarding-homescreen';

export function AuthScreen({ onSuccess }: AuthScreenProps) {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState<Step>(auth.currentUser ? (auth.currentUser.emailVerified ? 'onboarding-info' : 'email-verification') : 'auth');
  const [isLogin, setIsLogin] = useState(true);
  const [isResetMode, setIsResetMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [homeLocation, setHomeLocation] = useState('');
  const [dob, setDob] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showValidationNames, setShowValidationNames] = useState<string[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  // Home screen install step
  const showInstallGuide = import.meta.env.MODE !== 'native' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [homescreenOs, setHomescreenOs] = useState<'ios' | 'android'>('ios');
  const [linkCopied, setLinkCopied] = useState(false);

  // Profile Photo State
  const [image, setImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const joinPendingTrips = async (uid: string) => {
    try {
      const invitations = await getPendingInvitations();
      for (const invitation of invitations) {
        // Self-notification at signup: the new user (uid == caller) is a pending
        // invitee on this trip, so the callable authorizes it as an invite.
        await createTripNotifications({
          tripId: invitation.tripId,
          recipientIds: [uid],
          title: 'Trip Invitation',
          message: `You've been invited to join **${invitation.title}**`,
          type: 'invite',
          category: 'trip-admin',
          priority: 'P1',
          actionRequired: true,
          data: {
            tripTitle: invitation.title,
            creatorId: invitation.ownerId,
          },
        });
      }
    } catch (err) {
      console.error("Error processing pending trip invites:", err);
    }
  };

  const getErrorMessage = (error: any): string => {
    const code = error?.code || '';
    switch (code) {
      case 'auth/user-not-found':
        return isResetMode 
          ? "We don't have a record of that email address. Would you like to create an account instead?"
          : "We couldn't find an account with that email.";
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return "We couldn't find a match for those credentials. Please double-check your email and password.";
      case 'auth/email-already-in-use':
        return "It looks like you've already started a trip with us. Try signing in instead.";
      case 'auth/weak-password':
        return "For your security, please use a password with at least 6 characters.";
      case 'auth/too-many-requests':
        return "Access has been temporarily restricted due to many failed attempts. Please try again later.";
      case 'auth/network-request-failed':
        return "Connection lost. Please check your internet and try again.";
      case 'auth/invalid-email':
        return "That doesn't look like a valid email address.";
      default:
        return userMessage(error, "Something went wrong on our end. Please try again in a moment.");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    
    if (!email) {
      setError('Please enter your email address to receive reset instructions.');
      return;
    }

    setLoading(true);
    try {
      const sendReset = httpsCallable(functions, 'sendPasswordResetEmail');
      await sendReset({ email });
      setSuccessMessage('Instructions to reset your password have been sent to your email.');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setShowValidationNames([]);

    if (!email) setShowValidationNames(prev => [...prev, 'email']);
    if (!password) setShowValidationNames(prev => [...prev, 'password']);
    if (!email || !password) return;

    setLoading(true);

    try {
      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        
        // Check email verification first
        if (!userCredential.user.emailVerified) {
          // Let the user use their existing link or deliberately request another.
          setStep('email-verification');
          return;
        }

        const userDocRef = doc(db, 'users', userCredential.user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          
          if (!userData.onboardingCompleted) {
            setName(userData.name || '');
            setHomeLocation(userData.homeLocation || '');
            setDob(userData.dob || '');
            setCroppedImage(userData.imgSrc || userData.photoURL || null);
            setStep('onboarding-info');
          } else {
            await joinPendingTrips(userCredential.user.uid);
            await refreshUser();
            onSuccess(userData);
          }
        } else {
          setStep('onboarding-info');
        }
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await httpsCallable(functions, 'sendVerificationEmail')({ email: userCredential.user.email });
        
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          id: userCredential.user.uid,
          email: userCredential.user.email || email,
          createdAt: new Date().toISOString(),
          onboardingCompleted: false
        });
        
        setStep('email-verification');
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const checkEmailVerification = async () => {
    if (!auth.currentUser) return;
    setError(null);
    setSuccessMessage(null);
    setIsVerifying(true);
    try {
      await reload(auth.currentUser);
      if (auth.currentUser.emailVerified) {
        await refreshUser();
        setStep('onboarding-info');
      } else {
        setError("Email not verified yet. Please check your inbox and click the link.");
      }
    } catch (err: any) {
      setError(userMessage(err, "Couldn't check your verification. Check your connection and try again."));
    } finally {
      setIsVerifying(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (isSendingVerification || isVerifying) return;
    setError(null);
    setSuccessMessage(null);
    if (!auth.currentUser) {
      setError('Please sign in again to request a verification email.');
      return;
    }
    setIsSendingVerification(true);
    try {
      // Another tab may already have verified this account.
      await reload(auth.currentUser);
      if (auth.currentUser.emailVerified) {
        await refreshUser();
        setStep('onboarding-info');
        return;
      }
      await httpsCallable(functions, 'sendVerificationEmail')({ email: auth.currentUser.email });
      setSuccessMessage('Verification email sent. Use the newest email and check your spam folder.');
    } catch (err: any) {
      setError(err?.code === 'functions/resource-exhausted'
        ? 'Too many verification attempts. Please wait before requesting another email.'
        : err?.code === 'auth/network-request-failed'
          ? 'Check your connection, then try sending the email again.'
          : 'Could not send the verification email. Please try again later.');
    } finally {
      setIsSendingVerification(false);
    }
  };

  // Check for existing session on mount or auth change
  useEffect(() => {
    const syncStep = async () => {
      if (auth.currentUser && !loading) {
        // If they are logged in but reached sign-in, jump them to their state.
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          if (!auth.currentUser.emailVerified) {
            setStep('email-verification');
          } else if (!userData.onboardingCompleted) {
            setName(userData.name || '');
            setHomeLocation(userData.homeLocation || '');
            setDob(userData.dob || '');
            setCroppedImage(userData.imgSrc || null);
            setStep('onboarding-info');
          }
        }
      }
    };
    
    // Only jump if we are in the initial stages
    if (step === 'auth') {
      syncStep();
    }
  }, [auth.currentUser, loading]);

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowValidationNames([]);
    
    const missing = [];
    if (!name.trim()) missing.push('name');
    if (!homeLocation.trim()) missing.push('location');
    if (!dob) missing.push('dob');
    
    if (missing.length > 0) {
      setShowValidationNames(missing);
      return;
    }
    
    // Save current progress to Firestore
    if (auth.currentUser) {
      setLoading(true);
      try {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        // updateDoc would throw if the doc doesn't exist (the case for Auth
        // accounts whose user doc lives in another/abandoned database). Use
        // setDoc with merge:true and include the required fields the rule
        // checks (id, email, createdAt) so the create path also validates.
        const existingSnap = await getDoc(userDocRef);
        const existingCreatedAt = existingSnap.exists()
          ? (existingSnap.data() as { createdAt?: string }).createdAt
          : undefined;
        await setDoc(userDocRef, {
          id: auth.currentUser.uid,
          email: auth.currentUser.email || email,
          createdAt: existingCreatedAt || new Date().toISOString(),
          onboardingCompleted: false,
          name,
          homeLocation,
          dob,
        }, { merge: true });
        setStep('onboarding-photo');
      } catch (err: any) {
        setError(userMessage(err, "Couldn't save your details. Please try again."));
      } finally {
        setLoading(false);
      }
    } else {
      setStep('onboarding-photo');
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result as string);
        setIsCropping(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const showCroppedImage = useCallback(async () => {
    try {
      if (image && croppedAreaPixels) {
        const cropped = await getCroppedImg(image, croppedAreaPixels);
        setCroppedImage(cropped);
        setIsCropping(false);
      }
    } catch (e) {
      console.error(e);
    }
  }, [image, croppedAreaPixels]);

  const handleCompleteOnboarding = async () => {
    if (!auth.currentUser) return;
    setLoading(true);
    setError(null);

    try {
      const initials = (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
      const userDocRef = doc(db, 'users', auth.currentUser.uid);

      // Check whether the user doc already exists in this database. For pre-existing
      // Auth accounts whose user doc lives in another (e.g. abandoned) database, the
      // doc won't exist here — and the rule isValidUser requires createdAt on every
      // write. With merge:true, if the doc doesn't exist Firestore CREATES it and
      // we'd be missing the required createdAt field, silently failing the rule.
      const existingSnap = await getDoc(userDocRef);
      const existingCreatedAt = existingSnap.exists()
        ? (existingSnap.data() as { createdAt?: string }).createdAt
        : undefined;

      // Write profile data but do NOT set onboardingCompleted:true yet.
      // AuthContext has a live onSnapshot on this doc — writing onboardingCompleted:true
      // immediately would flip isFullyOnboarded in App.tsx and unmount AuthScreen
      // before we can show the homescreen install step. We finalise that flag in
      // finishOnboarding() below, either from the homescreen step buttons or the
      // non-mobile path.
      const profileData: any = {
        id: auth.currentUser.uid,
        name: name,
        email: auth.currentUser.email || email,
        homeLocation,
        dob,
        imgSrc: croppedImage,
        color: '#C5A059',
        emoji: '✈️',
        initials: initials || '??',
        createdAt: existingCreatedAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(userDocRef, profileData, { merge: true });

      if (name) {
        try {
          await updateProfile(auth.currentUser, { displayName: name });
        } catch (e) {
          console.warn("Failed to update firebase display name:", e);
        }
      }

      await joinPendingTrips(auth.currentUser.uid);

      // Small delay to allow Firestore to propagate before we proceed
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log("Profile saved for:", auth.currentUser.uid);

      // Only mobile web offers installation; native builds are already an app.
      // We stay on AuthScreen (onboardingCompleted still false) until the user dismisses
      // the step — finishOnboarding() then writes the flag and the onSnapshot transition
      // moves them into the app automatically.
      const ua = navigator.userAgent;
      if (showInstallGuide) {
        setHomescreenOs(/Android/i.test(ua) ? 'android' : 'ios');
        setStep('onboarding-homescreen');
      } else {
        await finishOnboarding();
      }
    } catch (err: any) {
      console.error("Onboarding error:", err);
      setError(userMessage(err, "Failed to save profile. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  // Writes onboardingCompleted:true, which the AuthContext onSnapshot picks up and
  // flips isFullyOnboarded in App.tsx — no explicit onSuccess call needed.
  const [finishingSetup, setFinishingSetup] = useState(false);
  const finishOnboarding = async () => {
    if (!auth.currentUser) return;
    setFinishingSetup(true);
    setLoading(true);
    setError(null);
    try {
      await setDoc(doc(db, 'users', auth.currentUser.uid), { onboardingCompleted: true }, { merge: true });
    } catch (e) {
      console.error("Error finalising onboarding:", e);
      setError(userMessage(e, 'Could not finish setup. Please try again.'));
    } finally {
      setFinishingSetup(false);
      setLoading(false);
    }
  };

  const backStep: Step | null =
    step === 'onboarding-info'       ? 'email-verification' :
    step === 'onboarding-photo'      ? 'onboarding-info'  :
    step === 'onboarding-homescreen' ? 'onboarding-photo' :
    null;

  const wizardSteps = [
    { id: 'email-verification', label: 'Email' },
    { id: 'onboarding-info', label: 'About you' },
    { id: 'onboarding-photo', label: 'Photo' },
    ...(showInstallGuide ? [{ id: 'onboarding-homescreen', label: 'Get the app' }] : []),
  ];
  const wizardIndex = wizardSteps.findIndex(s => s.id === step);
  const wizardHeader = wizardIndex >= 0 && (
    <div className="mb-6 space-y-3">
      <div className="flex items-center justify-between gap-3">
        {backStep && (
          <button
            type="button"
            aria-label="Back to previous step"
            disabled={loading}
            onClick={() => { setError(null); setSuccessMessage(null); setStep(backStep); }}
            className="min-h-11 min-w-11 flex shrink-0 items-center justify-center rounded-full text-app-sub hover:bg-app-faint transition-colors disabled:opacity-50"
          >
            <ArrowLeft size={22} aria-hidden="true" />
          </button>
        )}
        <p className="ds-label ml-auto text-right" aria-live="polite">Step {wizardIndex + 1} of {wizardSteps.length} · {wizardSteps[wizardIndex].label}</p>
      </div>
      <ol className="flex gap-2" aria-label="Account setup progress">
        {wizardSteps.map((s, index) => <li key={s.id} aria-current={index === wizardIndex ? 'step' : undefined} className={`h-1 flex-1 rounded-full ${index === wizardIndex ? 'bg-gold' : index < wizardIndex ? 'bg-gold/40' : 'bg-app-border/30'}`}><span className="sr-only">{s.label}{index < wizardIndex ? ' — completed' : ''}</span></li>)}
      </ol>
    </div>
  );

  return (
    <div className="auth-entry brand-loading-surface flex flex-col items-center justify-center p-6 relative overflow-hidden" style={{ minHeight: '100dvh' }}>
      <div className="auth-blobs"><CardTintStreaks /></div>
      {finishingSetup && <WizardLoadingScreen title="Finishing setup…" />}

      {/* Home-screen install step — full viewport, outside the constrained container */}
      <AnimatePresence>
        {showInstallGuide && step === 'onboarding-homescreen' && (
          <motion.div
            key="homescreen"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-4 z-50 mx-auto max-w-sm flex flex-col"
            style={{ height: 'calc(100dvh - 2rem)' }}
          >
            {wizardHeader}
            {/* Header: title + OS toggle */}
            <div className="flex-none pb-3 space-y-3">
              <div className="text-center text-balance">
                <h2 className="text-xl font-serif font-black text-app-text leading-snug">Keep your Talisman<br/>at your fingertips</h2>
                <p className="text-xs text-app-sub/60 mt-1 leading-relaxed">Access Talisman as an app directly from your home screen</p>
              </div>
              <div className="flex p-1 bg-app-faint rounded-2xl gap-1">
                {(['ios', 'android'] as const).map(os => (
                  <button
                    key={os}
                    onClick={() => setHomescreenOs(os)}
                    className={`flex-1 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${homescreenOs === os ? 'bg-white shadow-sm text-app-text' : 'text-app-sub/50'}`}
                  >
                    {os === 'ios' ? 'iPhone' : 'Android'}
                  </button>
                ))}
              </div>
            </div>

            {/* Content: embedded animation (iOS) or step cards (Android) */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {homescreenOs === 'ios' ? (
                <iframe
                  key="ios-anim"
                  srcDoc={iosInstallHtml}
                  title="iOS install guide"
                  className="w-full h-full"
                  style={{ border: 'none', background: 'transparent' }}
                  scrolling="no"
                />
              ) : (
                <div className="h-full overflow-y-auto px-6 py-4 flex flex-col gap-3">
                  {[
                    {
                      n: 1,
                      title: 'Open talismantravel.app in your browser',
                      desc: 'Works in Chrome, Firefox, Edge, Samsung Internet and most Android browsers.',
                      extra: (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('https://talismantravel.app').catch(() => {});
                            setLinkCopied(true);
                            setTimeout(() => setLinkCopied(false), 2500);
                          }}
                          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-all active:scale-95 w-full ${linkCopied ? 'bg-gold/10 border-gold/40' : 'bg-app-faint border-app-border/50'}`}
                        >
                          <span className={`text-xs font-semibold transition-colors ${linkCopied ? 'text-gold' : 'text-app-sub/70'}`}>
                            {linkCopied ? 'Copied!' : 'talismantravel.app'}
                          </span>
                          {linkCopied ? <Check className="w-3.5 h-3.5 text-gold shrink-0" /> : <Copy className="w-3.5 h-3.5 text-gold shrink-0" />}
                        </button>
                      ),
                    },
                    {
                      n: 2,
                      title: 'Open the browser menu',
                      desc: 'Tap the menu icon (⋮ or ≡) — usually top right — then tap "Add to Home Screen" or "Install App".',
                      extra: null as React.ReactNode,
                    },
                    {
                      n: 3,
                      title: 'Tap "Add" to confirm',
                      desc: 'Talisman will appear on your home screen, ready to launch like any other app.',
                      extra: null as React.ReactNode,
                    },
                  ].map(({ n, title, desc, extra }) => (
                    <div key={n} className="bg-white border border-app-border/30 rounded-2xl p-4 shadow-sm flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-gold text-white text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">{n}</div>
                      <div className="flex-1 flex flex-col gap-2">
                        <div className="text-sm font-bold text-app-text">{title}</div>
                        <p className="text-sm text-app-sub leading-relaxed">{desc}</p>
                        {extra}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action button */}
            <div className="flex-none pt-3" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
              {error && <p role="alert" className={`${AUTH_ERROR} mb-3`}>{error}</p>}
              <button
                onClick={finishOnboarding}
                disabled={loading}
                className="auth-primary w-full rounded-2xl py-3.5 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest shadow-sm active:scale-95 transition disabled:opacity-50"
              >
                {loading ? 'Finishing…' : 'Finish Setup'} <Check className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-sm w-full relative z-10">
        {step !== 'auth' && step !== 'onboarding-homescreen' && <>
          {wizardHeader}
          {step.startsWith('onboarding-') && error && <p role="alert" className={`${AUTH_ERROR} mb-4`}>{error}</p>}
        </>}
        <AnimatePresence mode="wait">
          {step === 'auth' && (
            <motion.div
              key="auth"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full relative"
            >
              {isResetMode && (
                <button
                  type="button"
                  onClick={() => {
                    setIsResetMode(false);
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="mb-5 min-h-11 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-app-sub hover:text-gold transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to sign in
                </button>
              )}
              <div className="text-center text-balance mb-8">
                {isResetMode ? (
                  <h1 className="mb-4"><TalismanLogo /></h1>
                ) : (
                  <>
                    <div className="flex justify-center mb-6">
                      <TalismanMark size={64} />
                    </div>
                    <h1 className="text-5xl font-serif font-black text-app-text mb-2">Talisman</h1>
                  </>
                )}
                <div className="space-y-1.5 px-4">
                  <p className="brand-tagline">
                    Every traveller's lucky charm
                  </p>
                </div>
              </div>

              <form onSubmit={isResetMode ? handleResetPassword : handleAuth} className="space-y-4">
                {error && (
                  <div role="alert" className={AUTH_ERROR}>
                    {error}
                  </div>
                )}
                {successMessage && (
                  <div className="p-4 bg-gold/5 border border-gold/20 rounded-2xl text-gold text-sm font-normal leading-relaxed text-center text-balance">
                    <p>{successMessage}</p>
                  </div>
                )}

                {!successMessage && (
                  <div className="space-y-4">
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-app-sub/40 group-focus-within:text-gold group-focus-within:opacity-100 transition" />
                      <input
                        type="email"
                        aria-label="Email"
                        name="email"
                        autoComplete="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (showValidationNames.includes('email')) setShowValidationNames(prev => prev.filter(n => n !== 'email'));
                        }}
                        className={`${FIELD_AUTH} pl-11 pr-4 ${fieldBorder(showValidationNames.includes('email'), true)}`}
                      />
                    </div>
                    
                    {!isResetMode && (
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-app-sub/40 group-focus-within:text-gold group-focus-within:opacity-100 transition" />
                        <input
                          type="password"
                          name="password"
                          autoComplete={isLogin ? 'current-password' : 'new-password'}
                          aria-label="Password"
                          placeholder="Password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (showValidationNames.includes('password')) setShowValidationNames(prev => prev.filter(n => n !== 'password'));
                          }}
                          className={`${FIELD_AUTH} pl-11 pr-4 ${fieldBorder(showValidationNames.includes('password'), true)}`}
                        />
                      </div>
                    )}
                  </div>
                )}

                {isLogin && !isResetMode && !successMessage && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsResetMode(true);
                        setError(null);
                        setSuccessMessage(null);
                      }}
                      className="text-[11px] font-medium text-app-sub hover:text-gold transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {!successMessage && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="auth-primary w-full rounded-2xl py-4 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest shadow-sm active:scale-95 transition disabled:opacity-50"
                  >
                    {loading ? (isResetMode ? 'Sending Link...' : isLogin ? 'Signing In...' : 'Creating Account...') : (isResetMode ? 'Send Reset Link' : isLogin ? 'Sign In' : 'Create Account')}
                    {isResetMode ? <RefreshCw className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  </button>
                )}
              </form>

              {!isResetMode && !successMessage && (
                <div className="mt-4 text-center text-balance">
                  <button
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setError(null);
                      setSuccessMessage(null);
                    }}
                    className="min-h-11 text-[11px] font-black uppercase tracking-wider text-gold hover:opacity-80 transition-opacity"
                  >
                    {isLogin ? "First time? Create your Talisman here" : "Already have an account? Sign In"}
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {step === 'email-verification' && (
            <motion.div
              key="verification"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="w-full text-center text-balance space-y-6"
            >
              <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Mail className="w-10 h-10 text-gold" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-serif font-black text-app-text">Verify your email</h2>
                <p className="text-sm text-app-sub font-medium leading-relaxed">
                  Check your inbox for a verification link for <span className="font-bold text-app-text">{auth.currentUser?.email || email}</span>.
                </p>
              </div>

              {error && (
                <div role="alert" className={AUTH_ERROR}>
                  {error}
                </div>
              )}
              {successMessage && <p role="status" className="text-sm text-app-sub">{successMessage}</p>}

              <div className="space-y-3 pt-4">
                <button
                  onClick={checkEmailVerification}
                  disabled={isSendingVerification || isVerifying}
                  className="auth-primary w-full rounded-2xl py-4 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest shadow-sm active:scale-95 transition disabled:opacity-50"
                >
                  {isVerifying ? 'Checking...' : 'I\'ve Verified It'} <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={resendVerificationEmail}
                  disabled={isSendingVerification || isVerifying}
                  className="min-h-11 text-[11px] font-black uppercase tracking-widest text-gold hover:opacity-80 transition-opacity disabled:opacity-50"
                >
                  {isSendingVerification ? 'Sending…' : 'Resend Email'}
                </button>
              </div>

              <button
                onClick={() => {
                  auth.signOut();
                  setStep('auth');
                }}
                className="w-full flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest text-app-sub/40 hover:text-app-text transition-colors pt-4 border-t border-app-border/10"
              >
                <ArrowLeft className="w-3 h-3" /> Use another account
              </button>
            </motion.div>
          )}

          {step === 'onboarding-info' && (
            <motion.div
              key="info"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center text-balance">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <UserIcon className="w-8 h-8 text-gold" />
                </div>
                <h2 className="text-2xl font-serif font-black text-app-text mb-1">Tell us about you</h2>
              </div>

              <form onSubmit={handleInfoSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="ds-label ml-1">Full Name</label>
                    <input
                      type="text"
                      aria-label="Full name"
                      name="name"
                      autoComplete="name"
                      placeholder=""
                      value={name}
                      maxLength={100}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (showValidationNames.includes('name')) setShowValidationNames(prev => prev.filter(n => n !== 'name'));
                      }}
                      className={`${FIELD_AUTH} px-5 ${fieldBorder(showValidationNames.includes('name'), true)}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="ds-label ml-1">Date of Birth</label>
                    <input
                      type="date"
                      aria-label="Date of birth"
                      name="dob"
                      autoComplete="bday"
                      value={dob}
                      onChange={(e) => {
                        setDob(e.target.value);
                        if (showValidationNames.includes('dob')) setShowValidationNames(prev => prev.filter(n => n !== 'dob'));
                      }}
                      className={`${FIELD_AUTH} px-5 ${fieldBorder(showValidationNames.includes('dob'), true)} [&::-webkit-datetime-edit]:opacity-40 ${!dob ? 'text-app-sub/40' : '[&::-webkit-datetime-edit]:opacity-100'}`}
                    />
                  </div>
                  <div className="relative space-y-1.5">
                    <label htmlFor="onboarding-home-city" className="ds-label ml-1">Home City</label>
                    <LocationSearch
                      id="onboarding-home-city"
                      value={homeLocation}
                      onChange={(v) => {
                        setHomeLocation(v);
                        if (showValidationNames.includes('location')) setShowValidationNames(prev => prev.filter(n => n !== 'location'));
                      }}
                      placeholder="Search"
                      error={showValidationNames.includes('location')}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !name.trim() || !homeLocation.trim() || !dob}
                  className="auth-primary w-full rounded-2xl py-4 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest shadow-sm active:scale-95 transition disabled:opacity-50"
                >
                  {loading ? 'Saving…' : 'Continue'} <ChevronRight className="w-4 h-4" />
                </button>
              </form>
            </motion.div>
          )}

          {step === 'onboarding-photo' && (
            <motion.div
              key="photo"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center text-balance">
                <h2 className="text-2xl font-serif font-black text-app-text mb-1">Add a profile photo</h2>
                <div className="space-y-1 mb-8">
                  <p className="text-sm font-sans text-app-sub/40">Your photo will be visible to other travellers you collaborate with.</p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-8">
                <div className="flex flex-col items-center gap-3">
                  <div className="relative group">
                    <div className={`w-40 h-40 rounded-full border-2 border-dashed border-app-border/40 overflow-hidden flex items-center justify-center bg-white shadow-inner relative transition-colors ${croppedImage ? 'border-gold/20' : 'hover:border-gold/40'}`}>
                      {croppedImage ? (
                        <img src={croppedImage} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-10 h-10 text-app-sub/40" />
                      )}
                      <label className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/0 hover:bg-black/5 transition focus-within:ring-2 focus-within:ring-inset focus-within:ring-gold">
                        <input type="file" aria-label="Choose profile photo" value="" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                    {croppedImage && (
                      <label className="absolute -top-2 -right-2 w-11 h-11 bg-gold text-white border-4 border-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition cursor-pointer focus-within:ring-2 focus-within:ring-gold">
                        <input type="file" aria-label="Change profile photo" value="" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" onChange={handleImageUpload} />
                        <Edit2 className="w-4 h-4" />
                      </label>
                    )}
                  </div>
                  {!croppedImage && (
                    <label className="relative min-h-11 flex items-center cursor-pointer text-[11px] font-black uppercase tracking-widest text-gold text-center active:opacity-70 transition-opacity focus-within:ring-2 focus-within:ring-gold">
                      <input type="file" aria-label="Upload profile photo" value="" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept="image/*" onChange={handleImageUpload} />
                      Upload Photo
                    </label>
                  )}
                </div>

                <div className="w-full space-y-4">
                  <button
                    onClick={handleCompleteOnboarding}
                    disabled={loading}
                    className="auth-primary w-full rounded-2xl py-4 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest shadow-sm active:scale-95 transition disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Continue'} <ChevronRight className="w-4 h-4" />
                  </button>
                  {/* Skip Option */}
                  {!croppedImage && (
                    <button
                      onClick={handleCompleteOnboarding}
                      disabled={loading}
                      className="w-full py-2 text-[11px] font-black uppercase tracking-widest text-app-sub hover:text-app-text transition-colors"
                    >
                      Skip for now
                    </button>
                  )}
                </div>
              </div>

              {/* Cropping Modal Overlay */}
                {isCropping && image && createPortal(
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] bg-black/90 flex flex-col p-6"
                  >
                    <div className="flex-1 relative mt-10 mb-20">
                      <LazyCropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>
                    
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <input
                          type="range"
                          aria-label="Photo zoom"
                          name="photo-zoom"
                          value={zoom}
                          min={1}
                          max={3}
                          step={0.1}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="w-full accent-gold"
                        />
                        <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-white/40">
                          <span>Zoom</span>
                          <span>{zoom.toFixed(1)}x</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <button
                          onClick={() => setIsCropping(false)}
                          className="flex-1 bg-white/10 text-white rounded-2xl py-4 text-[11px] font-black uppercase tracking-widest hover:bg-white/20 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={showCroppedImage}
                          className="flex-1 bg-gold text-white rounded-2xl py-4 text-[11px] font-black uppercase tracking-widest shadow-xl shadow-gold/20 hover:brightness-110 transition"
                        >
                          Apply Crop
                        </button>
                      </div>
                    </div>
                  </motion.div>,
                  document.getElementById('import-modal-root') ?? document.body
                )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
